import { Component, EventEmitter, Input, Output } from '@angular/core';
import { deepCopy, removeUndefined, ValidationData, isDateTime } from 'na-lib';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SettingsService } from 'src/app/service/settings.service';
import { MatDialog } from '@angular/material/dialog';
import { WorkTaskTemplateFieldRuleDialogComponent } from 'src/app/worktask/work-task-template-field-rule-dialog/work-task-template-field-rule-dialog.component';
import { TranslateService } from '@ngx-translate/core';


@Component({
    selector: '[app-work-task-template-field-rules]',
    templateUrl: './work-task-template-field-rules.component.html',
    styleUrl: './work-task-template-field-rules.component.css',
    standalone: false
})
export class WorkTaskTemplateFieldRulesComponent {
  @Input('app-work-task-template-field-rules') data:any;
  @Output() returnChanges = new EventEmitter<{newRules: any, field: any, fieldId:string}>()
  validationAll:any
  validationForm!:FormGroup
  fieldType:string
  rulesExpanded = false
  private rulesExpansionInitialized = false
  ruleTypes:any = [
    {//textLength
      type: 'textLength',
      inputType:'number',
      icon:'text_format',
      translation:"taskTemplates.rules.textLength",
      active:true,
      allowedFields:['text', 'longText'],
      parameters:[
        {
          key:'min',
          type:'number',
          description:"taskTemplates.rules.textLength_min_description",
          inputType:'number',
        },
        {
          key:'max',
          type:'number',
          description:"taskTemplates.rules.textLength_max_description",
          inputType:'number'
        }
      ],
      description:"taskTemplates.rules.textLength_description"
    },
    {// german phone
      type: 'germanPhoneNumber',
      inputType: 'text',
      icon: 'phone',
      translation: 'taskTemplates.rules.germanPhoneNumber',
      active: true,
      allowedFields: ['text','longText'],
      description: 'taskTemplates.rules.GermanPhoneNumber_description',
      regex:'^(?:\+49|0)(?:\s*\(?\d{2,5}\)?\s*)[\d\s-]{5,12}$'
    },
    {// URL
      type: 'url',
      inputType: 'text',
      icon: 'link',
      translation: 'taskTemplates.rules.url',
      active: true,
      allowedFields: ['text','longText'],
      description: 'taskTemplates.rules.URL_description',
      regex: '^(https?:\\/\\/)?([\\da-zA-Z\\.-]+)\\.([a-zA-Z\\.]{2,})([\\/\\w \\.-]*)*\\/?*$'
    },
    {// PLZ
      type: 'plz',
      inputType: 'text',
      icon: 'numbers',
      translation: 'taskTemplates.rules.plz',
      active: true,
      allowedFields: ['text','longText'],
      description: 'taskTemplates.rules.PLZ_description',
      regex: '^\\d{5}$'
    },
    {// no spaces
      type: 'noSpaces',
      inputType: 'text',
      icon: 'text_format',
      translation: 'taskTemplates.rules.noSpaces',
      active: true,
      allowedFields: ['text','longText'],
      description: 'taskTemplates.rules.NoSpaces_description',
      regex: '^[a-zA-Z]+$'},
    {// email (predefined textRegex)
      type: 'email',
      inputType: 'text',
      icon: 'alternate_email',
      translation: 'taskTemplates.rules.email',
      active: true,
      allowedFields: ['text','longText'],
      description: 'taskTemplates.rules.email_description'
    },
    {
      type: 'emailLowercase',
      inputType: 'text',
      icon: 'alternate_email',
      translation: 'taskTemplates.rules.emailLowercase',
      active: true,
      allowedFields: ['text','longText'],
      description: 'taskTemplates.rules.email_description'
    },
    {//textRegex
      type: 'textRegex',
      inputType:'text',
      icon:'checklist',
      translation:"taskTemplates.rules.textRegex",
      active:true,
      allowedFields:['text', 'longText','weight'],
      parameters:[
        {
          key:'regex',
          type:'string',
          description:"taskTemplates.rules.textRegex_regex_description",
          inputType:'text',
        }
      ],
      description:"taskTemplates.rules.textRegex_description"
    },
    {
      type: 'textEnum',
      icon: 'list_alt',
      translation: 'taskTemplates.rules.textEnum',
      // Keep the implementation for existing data, but do not offer it until
      // all supported devices can validate the new rule types.
      hiddenUntilDeviceSupport: true,
      allowedFields: ['text', 'longText'],
      parameters: [{ key: 'values', description: 'taskTemplates.rules.enum', inputType: 'textarea' }]
    },
    {
      type: 'textFormat',
      icon: 'format_align_justify',
      translation: 'taskTemplates.rules.textFormat',
      hiddenUntilDeviceSupport: true,
      allowedFields: ['text', 'longText'],
      parameters: [{ key: 'format', description: 'taskTemplates.rules.format', inputType: 'format' }]
    },
    {
      type: 'numericEnum',
      icon: 'format_list_numbered',
      translation: 'taskTemplates.rules.numericEnum',
      hiddenUntilDeviceSupport: true,
      allowedFields: ['number', 'decimal', 'weight'],
      parameters: [{ key: 'values', description: 'taskTemplates.rules.enum', inputType: 'textarea' }]
    },
    {//numericRange
      type: 'numericRange',
      inputType:'number',
      icon:'pin',
      translation:"taskTemplates.rules.numericRange",
      active:true,
      allowedFields:['number', 'decimal', 'weight', 'text', 'longText'],
      parameters:[
        {
          key:'min',
          type:'number',
          description:"taskTemplates.rules.numericRange_min_description",
          inputType:'number',
        },
        {
          key:'max',
          type:'number',
          description:"taskTemplates.rules.numericRange_max_description",
          inputType:'number'
        },
        {
          key:'multipleOf',
          description:'taskTemplates.rules.multipleOf',
          inputType:'number'
        },
        {
          key:'maxExclusive',
          description:'taskTemplates.rules.maxExclusive',
          inputType:'boolean'
        },
        {
          key:'decimal',
          type:'boolean',
          description:"taskTemplates.rules.numericRange_decimal_description",
          inputType:'number'
        },
        {
          key:'minExclusive',
          description:'taskTemplates.rules.minExclusive',
          inputType:'boolean'
        }
      ],
      description:"taskTemplates.rules.numericRange_description"
    },
    {//dateRange
      type: 'dateRange',
      icon:'calendar_month',
      translation:"taskTemplates.rules.dateRange",
      active:true,
      allowedFields:['date', 'dateTime', 'time'],
      parameters:[
        {
          key:'min',
          type:'date',
          description:"taskTemplates.rules.dateRange_min_description",
          inputType:'datetime-local',

        },
        {
          key:'max',
          type:'date',
          description:"taskTemplates.rules.dateRange_max_description",
          inputType:'datetime-local',

        },
        {
          key:'minDaysAgo',
          type:'number',
          description:"taskTemplates.rules.dateRange_minDaysAgo_description",
          inputType:'number',

        }
      ],
      description:"taskTemplates.rules.dateRange_description"
    }
  ]

  tasktemplateUserRights_update:boolean
  tasktemplateUserRights_delete:boolean

  get selectableRuleTypes(): any[] {
    return this.ruleTypes.filter(rule => !rule.hiddenUntilDeviceSupport);
  }

  constructor(private fb: FormBuilder,
    private settingService: SettingsService,
    public dialog: MatDialog,
    private translate: TranslateService
    ){
    this.validationForm = this.fb.group({
      validations:[]
    })
  }

  ngOnInit() : void{
    this.tasktemplateUserRights_update = this.settingService.modulesRolesRights$.value['workTaskTemplates']['U']
    this.tasktemplateUserRights_delete = this.settingService.modulesRolesRights$.value['workTaskTemplates']['D']
    if(this.data.type)
    this.fieldType = this.data.type

    this.validationForm.valueChanges.subscribe(value =>{
      this.returnChanges.emit({newRules: this.validationAll, field: this.data, fieldId: this.data.id})
    })

  }

  ngOnChanges(){
    this.validationAll = deepCopy(this.data?.validations || [])
    this.fieldType = this.data?.type
    if (!this.rulesExpansionInitialized) {
      this.rulesExpanded = this.validationAll.length > 0
      this.rulesExpansionInitialized = true
    }
    if(JSON.stringify(this.validationAll) != JSON.stringify(this.validationForm.value.validations))
    this.validationForm.patchValue({validations: this.validationAll})
  }

  editRule(rule, index){
    const ruleStructure = this.ruleTypes.find(ruleType => ruleType.type == rule.type)

    const dialogRef = this.dialog.open(WorkTaskTemplateFieldRuleDialogComponent, {
      // width: '400px',
      data: { rule, ruleStructure , fieldType: this.fieldType}
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result?.[0]){
        this.validationAll[index]= result[1]
        this.validationForm.patchValue({validations: this.validationAll})
      }
    });
  }

  deleteRule(rule, index){
    this.validationAll.splice(index,1)
    this.validationForm.patchValue({validations: this.validationAll})
  }

  addRule(type){
    let newRule = deepCopy(ValidationData[type] || { type });

    if (type === 'email') {
      newRule = {
        type: 'textRegex',
        regex: '^[\\w.%+-]+@[\\w.-]+\\.[A-Za-z]{2,}$'
      };
    }
    if (type === 'emailLowercase') {
      newRule = {
        type: 'textRegex',
        regex: '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$'
      };
    }
    if (type === 'germanPhoneNumber') {
      newRule = {
        type: 'textRegex',
        regex: '^\\+?49\\s?\\(\\d{3}\\)?\\s?\\d{9,10}$'
      };
    }
    if (type === 'url') {
      newRule = {
        type: 'textRegex',
        regex: '^(https?:\\/\\/)?([\\da-zA-Z\\.-]+)\\.([a-zA-Z\\.]{2,})([\\/\\w \\.-]*)*\\/?*$'
      };
    }
    if (type === 'plz') {
      newRule = {
        type: 'textRegex',
        regex: '^\\d{5}$'
      };
    }
    if (type === 'noSpaces') {
      newRule = {
        type: 'textRegex',
        regex: '^[a-zA-Z]+$'
      };
    }

    removeUndefined(newRule);
    const ruleStructure = this.ruleTypes.find(ruleType => ruleType.type === type);
    if (ruleStructure?.parameters?.length) {
      const dialogRef = this.dialog.open(WorkTaskTemplateFieldRuleDialogComponent, {
        data: { rule: newRule, ruleStructure, fieldType: this.fieldType }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result?.[0]) {
          this.validationAll.push(result[1]);
          this.validationForm.patchValue({ validations: this.validationAll });
        }
      });
      return;
    }
    this.validationAll.push(newRule);
    this.validationForm.patchValue({ validations: this.validationAll });
  }

  checkIfRuleIsAddedAlready(rule){
    return this.validationAll.find(ruleAdded => ruleAdded.type == rule.type)
  }

  getRuleIcon(type: string): string {
    return this.ruleTypes.find(rule => rule.type === type)?.icon || 'rule';
  }

  getRuleLabel(type: string): string {
    const key = `contentTypes.schemaRuleLabels.${type}`;
    const label = this.translate.instant(key);
    return label === key ? this.translate.instant(`taskTemplates.rules.${type}`) : label;
  }

  getRuleSummary(rule: any): string {
    const t = (key: string, params?: any) => this.translate.instant(key, params);
    const has = (value: any) => value !== undefined && value !== null && value !== '';
    const range = (label: string) => {
      if (has(rule.min) && has(rule.max)) return t('contentTypes.schemaRuleSummary.range', { label, min: rule.min, max: rule.max });
      if (has(rule.min)) return t('contentTypes.schemaRuleSummary.minimum', { label, min: rule.min });
      if (has(rule.max)) return t('contentTypes.schemaRuleSummary.maximum', { label, max: rule.max });
      return '';
    };
    if (rule.type === 'textLength') return range(t('contentTypes.schemaRuleSummary.characters'));
    if (rule.type === 'numericRange') {
      return [range(t('contentTypes.schemaRuleSummary.value')),
        rule.minExclusive ? t('taskTemplates.rules.minExclusive') : '',
        rule.maxExclusive ? t('taskTemplates.rules.maxExclusive') : '',
        has(rule.multipleOf) ? `${t('taskTemplates.rules.multipleOf')}: ${rule.multipleOf}.` : '',
        rule.decimal === false ? t('contentTypes.schemaRuleSummary.wholeNumbers') : ''].filter(Boolean).join(' ') || t('taskTemplates.rules.numericOnly');
    }
    if (rule.type === 'textEnum' || rule.type === 'numericEnum') {
      const values = Array.isArray(rule.values) ? rule.values : [];
      if (!values.length) return t('contentTypes.schemaRuleSummary.setAllowedValues');
      return values.length <= 4
        ? t('contentTypes.schemaRuleSummary.allowedValues', { values: values.join(', ') })
        : t('contentTypes.schemaRuleSummary.allowedValuesCount', { count: values.length });
    }
    if (rule.type === 'textFormat') return t('contentTypes.schemaRuleSummary.formatRequired', { format: rule.format });
    if (rule.type === 'textRegex') return rule.regex || t('contentTypes.schemaRuleSummary.patternRequired');
    if (rule.type === 'dateRange') {
      const limits = [has(rule.min) ? `${t('taskTemplates.rules.min')}: ${this.showDate(rule.min)}` : '',
        has(rule.max) ? `${t('taskTemplates.rules.max')}: ${this.showDate(rule.max)}` : '',
        has(rule.minDaysAgo) ? this.showMinDaysAgoLabel(rule.minDaysAgo) : ''];
      return limits.filter(Boolean).join(' · ');
    }
    return t('contentTypes.schemaRuleSummary.restricted');
  }


  showDate(date){
    return isDateTime(date)
  }

  showMinDaysAgoLabel(minDaysAgo){
    const message = this.translate.instant('taskTemplates.rules.minDaysAgo')
    const label = message.replace('<number>', minDaysAgo)
    return label
  }
}
